import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { documentTitle as buildDocumentTitle } from 'constants/app-info';
import logo from 'assets/images/hp-logo.png';
import { authTheme } from 'theme/antd-theme';
import AuthFooter from './AuthFooter';
import BrandPanel from './BrandPanel';
import EnvBadge from './EnvBadge';
import styles from './auth-layout.module.scss';

export interface AuthLayoutProps {
  /** H1 của thẻ (mỗi trang đúng MỘT h1, id="auth-title"). Cũng dùng cho tiêu đề tab. */
  title: string;
  /** Dòng mô tả dưới H1 (14/20, màu inkSoft). */
  subtitle?: ReactNode;
  /** Dòng nhỏ viết hoa cạnh logo, trên H1. Mặc định "HP MEDIA · QUẢN TRỊ". */
  eyebrow?: string;
  /** Hiện "Bước {current}/{total}" bên phải eyebrow. */
  step?: { current: number; total: number };
  /** Thay chữ bên phải eyebrow (vd. "Hoàn tất"). Ưu tiên hơn `step`. */
  eyebrowAside?: ReactNode;
  /** Link "← {label}" phía trên eyebrow, vd. { to: '/login', label: 'Quay lại đăng nhập' }. */
  backTo?: { to: string; label: string; state?: unknown };
  /** Chip dưới phụ đề — dùng <AuthChip>. Chỉ trang Đăng nhập. */
  badge?: ReactNode;
  /** Dải xám cuối thẻ — thường là một hoặc vài <AuthSwitch>. */
  cardFooter?: ReactNode;
  /** Tiêu đề tab nếu khác `title`. Luôn có hậu tố "· Quản trị HP Media" và tiền tố môi trường. */
  documentTitle?: string;
  /** Form / nội dung thân thẻ. */
  children: ReactNode;
}

const DEFAULT_EYEBROW = 'HP Media · Quản trị';

/**
 * Khung chung của mọi trang xác thực (Đăng nhập, Đăng ký, Quên mật khẩu, Yêu cầu quyền).
 *
 * Dải đầu thẻ khớp AuthHeader của app (dải màu Hoàng hôn 28/09): nền Lem nhạt, bo dưới-phải 40,
 * logo gốc 36×36 + dòng "HP MEDIA · QUẢN TRỊ" (accentDeep), H1 22/28 màu heading, phụ đề inkSoft.
 *
 * - ≥900px: cột thương hiệu nền Đêm (BrandPanel) + cột form, thẻ 440px căn giữa. Logo trong
 *   dải đầu thẻ bị ẩn (cụm logo đã ở cột thương hiệu), chỉ còn dòng overline.
 * - <900px: chỉ còn thẻ, bám lên đầu; dải đầu thẻ là khối thương hiệu duy nhất (logo + EnvBadge
 *   trong hàng overline) — không thêm thanh đầu trang để khỏi lặp logo hai lần.
 * - <576px: thẻ "phẳng" tràn ngang, dải Lem nhạt nằm sát mép trên như màn đăng nhập của app.
 *
 * Bọc ConfigProvider(authTheme): ô/nút size="large" cao 48, Form.Item cách nhau 20.
 */
const AuthLayout = ({
  title,
  subtitle,
  eyebrow = DEFAULT_EYEBROW,
  step,
  eyebrowAside,
  backTo,
  badge,
  cardFooter,
  documentTitle,
  children,
}: AuthLayoutProps) => {
  const tabTitle = documentTitle || title;

  useEffect(() => {
    document.title = buildDocumentTitle(tabTitle);
  }, [tabTitle]);

  // Rời khu xác thực thì trả tiêu đề tab về mặc định (các trang trong vỏ admin không tự đặt).
  useEffect(
    () => () => {
      document.title = buildDocumentTitle();
    },
    []
  );

  const aside = eyebrowAside ?? (step ? `Bước ${step.current}/${step.total}` : null);

  return (
    <ConfigProvider theme={authTheme}>
      <div className={styles.page}>
        <BrandPanel />

        <div className={styles.column}>
          <main className={styles.main}>
            <section className={styles.card} aria-labelledby="auth-title">
              <div className={styles.cardHead}>
                {backTo && (
                  <Link to={backTo.to} state={backTo.state} className={styles.back}>
                    <ArrowLeftOutlined aria-hidden="true" />
                    {backTo.label}
                  </Link>
                )}
                <div className={styles.brandRow}>
                  {/* Logo giữ nguyên, không pha màu; alt rỗng vì ngay cạnh đã có chữ HP Media. */}
                  <img className={styles.headLogo} src={logo} width={36} height={36} alt="" />
                  <span className={styles.eyebrow}>{eyebrow}</span>
                  <EnvBadge className={styles.headEnv} />
                  {aside ? <span className={styles.eyebrowAside}>{aside}</span> : null}
                </div>
                <h1 id="auth-title" className={styles.title}>
                  {title}
                </h1>
                {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
                {badge ? <div className={styles.badgeRow}>{badge}</div> : null}
              </div>

              <div className={styles.cardBody}>{children}</div>

              {cardFooter ? <div className={styles.cardFooter}>{cardFooter}</div> : null}
            </section>
          </main>

          <AuthFooter />
        </div>
      </div>
    </ConfigProvider>
  );
};

export default AuthLayout;
